/**
 * AEOLeadCaptureForm.tsx
 * Lead capture form offering the complete AEO & LLMO Buyer's Guide PDF.
 * Renders on all five AEO/LLMO buyer's guide series posts.
 */

import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { BookOpen, Download, CheckCircle, Loader2, FileText, Lock, Linkedin, Twitter, Link2, Check } from "lucide-react";
import { trackFormSubmit, trackFormSuccess, trackFormError } from "@/lib/analytics";

interface Props {
  sourceSlug?: string;
}

// ── Success state sub-component ─────────────────────────────────────────────
function SuccessState({ pdfUrl, sourceSlug }: { pdfUrl: string | null; sourceSlug?: string }) {
  const [copied, setCopied] = useState(false);

  const shareUrl = sourceSlug
    ? `https://integrated.social/blog/${sourceSlug}/`
    : "https://integrated.social/blog/";

  const shareText = encodeURIComponent(
    "Just downloaded the Complete AEO & LLMO Buyer's Guide 2026 - covers agency selection, UK pricing benchmarks, ROI measurement, and the in-house vs agency decision. Free PDF:"
  );

  const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
  const twitterUrl = `https://twitter.com/intent/tweet?text=${shareText}&url=${encodeURIComponent(shareUrl)}&via=MoElnadi`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // fallback: select text
    }
  };

  return (
    <div className="text-center py-4">
      {/* Checkmark */}
      <div
        className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
        style={{ background: "rgba(34,197,94,0.15)", border: "1px solid rgba(34,197,94,0.3)" }}
      >
        <CheckCircle className="w-8 h-8" style={{ color: "#22C55E" }} />
      </div>

      <h3
        className="text-xl font-bold mb-2"
        style={{ color: "#F0F4F8", fontFamily: "Georgia, serif" }}
      >
        Your guide is on its way.
      </h3>
      <p className="text-sm mb-5" style={{ color: "#8899AA" }}>
        Check your inbox for the PDF download link. Or download it right now.
      </p>

      {/* Primary CTA - download */}
      {pdfUrl && (
        <a
          href={pdfUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 rounded-lg px-6 py-3 text-sm font-bold mb-6"
          style={{ background: "#FF8C00", color: "#0F1419" }}
        >
          <Download className="w-4 h-4" />
          Download PDF Now
        </a>
      )}

      {/* Social sharing divider */}
      <div className="flex items-center gap-3 my-5">
        <div className="flex-1 h-px" style={{ background: "rgba(255,255,255,0.08)" }} />
        <span className="text-xs font-medium" style={{ color: "#556677" }}>Share with your network</span>
        <div className="flex-1 h-px" style={{ background: "rgba(255,255,255,0.08)" }} />
      </div>

      {/* Share buttons */}
      <div className="flex items-center justify-center gap-3 mb-6">
        {/* LinkedIn */}
        <a
          href={linkedInUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Share on LinkedIn"
          className="flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium transition-all"
          style={{
            background: "rgba(10,102,194,0.15)",
            border: "1px solid rgba(10,102,194,0.35)",
            color: "#5BA4D4",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(10,102,194,0.25)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(10,102,194,0.15)"; }}
        >
          <Linkedin className="w-4 h-4" />
          LinkedIn
        </a>

        {/* X / Twitter */}
        <a
          href={twitterUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Share on X"
          className="flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium transition-all"
          style={{
            background: "rgba(255,255,255,0.06)",
            border: "1px solid rgba(255,255,255,0.15)",
            color: "#C8D4E0",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.12)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.06)"; }}
        >
          <Twitter className="w-4 h-4" />
          X
        </a>

        {/* Copy link */}
        <button
          type="button"
          onClick={handleCopy}
          aria-label="Copy link to clipboard"
          className="flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium transition-all"
          style={{
            background: copied ? "rgba(34,197,94,0.12)" : "rgba(255,255,255,0.06)",
            border: copied ? "1px solid rgba(34,197,94,0.35)" : "1px solid rgba(255,255,255,0.15)",
            color: copied ? "#22C55E" : "#C8D4E0",
            cursor: "pointer",
          }}
          onMouseEnter={(e) => {
            if (!copied) e.currentTarget.style.background = "rgba(255,255,255,0.12)";
          }}
          onMouseLeave={(e) => {
            if (!copied) e.currentTarget.style.background = "rgba(255,255,255,0.06)";
          }}
        >
          {copied ? <Check className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
          {copied ? "Copied!" : "Copy link"}
        </button>
      </div>

      {/* Audit CTA */}
      <p className="text-xs" style={{ color: "#556677" }}>
        Want a free AI visibility readiness score for your website?{" "}
        <a href="/free-ai-growth-audit/" className="underline" style={{ color: "#FF8C00" }}>
          Check my AI visibility
        </a>
      </p>
    </div>
  );
}

type FormState = "idle" | "submitting" | "success" | "error";

export function AEOLeadCaptureForm({ sourceSlug }: Props) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [formState, setFormState] = useState<FormState>("idle");
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState("");

    const submitLead = trpc.aeoGuide.submitLead.useMutation({
    onSuccess: (data) => {
      setPdfUrl(data.pdfUrl);
      setFormState("success");
      trackFormSuccess({ form_name: "aeo_guide_download", form_location: sourceSlug ?? "blog_post" });
    },
    onError: (err) => {
      setErrorMsg(err.message || "Something went wrong. Please try again.");
      setFormState("error");
      trackFormError({ form_name: "aeo_guide_download", form_location: sourceSlug ?? "blog_post", error_message: err.message });
    },
  });
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return;
    setFormState("submitting");
    setErrorMsg("");
    trackFormSubmit({ form_name: "aeo_guide_download", form_location: sourceSlug ?? "blog_post" });
    submitLead.mutate({
      name: name.trim(),
      email: email.trim(),
      company: company.trim() || undefined,
      sourceSlug,
    });
  };

  return (
    <section
      aria-label="Download the complete AEO and LLMO buyer's guide PDF"
      className="my-12 rounded-xl overflow-hidden border border-amber-800/40"
      style={{ background: "linear-gradient(135deg, #1A1F2E 0%, #0F1419 60%, #1A1208 100%)" }}
    >
      {/* Top accent bar */}
      <div className="h-1 w-full" style={{ background: "linear-gradient(90deg, #FF8C00, #FFB347)" }} />

      <div className="p-8 md:p-10">
        {formState !== "success" ? (
          <>
            {/* Header */}
            <div className="flex items-start gap-4 mb-6">
              <div
                className="flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center"
                style={{ background: "rgba(255,140,0,0.15)", border: "1px solid rgba(255,140,0,0.3)" }}
              >
                <BookOpen className="w-6 h-6" style={{ color: "#FF8C00" }} />
              </div>
              <div>
                <p
                  className="text-xs font-bold uppercase tracking-widest mb-1"
                  style={{ color: "#FF8C00" }}
                >
                  Free Download · Complete Guide
                </p>
                <h3
                  className="text-xl md:text-2xl font-bold leading-tight"
                  style={{ color: "#F0F4F8", fontFamily: "Georgia, serif" }}
                >
                  The Complete AEO &amp; LLMO Buyer's Guide 2026
                </h3>
                <p className="mt-1 text-sm" style={{ color: "#8899AA" }}>
                  All five parts in one PDF - agency selection, UK pricing benchmarks, ROI measurement, and the in-house vs agency decision framework.
                </p>
              </div>
            </div>

            {/* What's inside */}
            <div
              className="rounded-lg p-4 mb-6 grid grid-cols-1 md:grid-cols-2 gap-2"
              style={{ background: "rgba(255,140,0,0.06)", border: "1px solid rgba(255,140,0,0.15)" }}
            >
              {[
                "Part 1 · What does an AEO agency do?",
                "Part 2 · How to choose the right agency",
                "Part 3 · UK pricing benchmarks & ROI data",
                "Part 4 · How to measure AEO results",
                "Part 5 · Agency vs in-house decision matrix",
                "Appendix · 12-question evaluation checklist",
              ].map((item) => (
                <div key={item} className="flex items-center gap-2">
                  <FileText className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "#FF8C00" }} />
                  <span className="text-xs" style={{ color: "#C8D4E0" }}>
                    {item}
                  </span>
                </div>
              ))}
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label
                    htmlFor="aeo-lead-name"
                    className="block text-xs font-medium mb-1"
                    style={{ color: "#8899AA" }}
                  >
                    Full name <span style={{ color: "#FF8C00" }}>*</span>
                  </label>
                  <input
                    id="aeo-lead-name"
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Jane Smith"
                    className="w-full rounded-lg px-4 py-2.5 text-sm outline-none transition-all"
                    style={{
                      background: "rgba(255,255,255,0.05)",
                      border: "1px solid rgba(255,255,255,0.12)",
                      color: "#F0F4F8",
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = "#FF8C00";
                      e.currentTarget.style.boxShadow = "0 0 0 3px rgba(255,140,0,0.15)";
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = "rgba(255,255,255,0.12)";
                      e.currentTarget.style.boxShadow = "none";
                    }}
                  />
                </div>
                <div>
                  <label
                    htmlFor="aeo-lead-email"
                    className="block text-xs font-medium mb-1"
                    style={{ color: "#8899AA" }}
                  >
                    Work email <span style={{ color: "#FF8C00" }}>*</span>
                  </label>
                  <input
                    id="aeo-lead-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="jane@company.com"
                    className="w-full rounded-lg px-4 py-2.5 text-sm outline-none transition-all"
                    style={{
                      background: "rgba(255,255,255,0.05)",
                      border: "1px solid rgba(255,255,255,0.12)",
                      color: "#F0F4F8",
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = "#FF8C00";
                      e.currentTarget.style.boxShadow = "0 0 0 3px rgba(255,140,0,0.15)";
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = "rgba(255,255,255,0.12)";
                      e.currentTarget.style.boxShadow = "none";
                    }}
                  />
                </div>
              </div>
              <div>
                <label
                  htmlFor="aeo-lead-company"
                  className="block text-xs font-medium mb-1"
                  style={{ color: "#8899AA" }}
                >
                  Company <span style={{ color: "#556677" }}>(optional)</span>
                </label>
                <input
                  id="aeo-lead-company"
                  type="text"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  placeholder="Acme Corp"
                  className="w-full rounded-lg px-4 py-2.5 text-sm outline-none transition-all"
                  style={{
                    background: "rgba(255,255,255,0.05)",
                    border: "1px solid rgba(255,255,255,0.12)",
                    color: "#F0F4F8",
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = "#FF8C00";
                    e.currentTarget.style.boxShadow = "0 0 0 3px rgba(255,140,0,0.15)";
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = "rgba(255,255,255,0.12)";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                />
              </div>

              {formState === "error" && (
                <p className="text-sm rounded-lg px-4 py-2" style={{ color: "#FF6B6B", background: "rgba(255,107,107,0.1)", border: "1px solid rgba(255,107,107,0.2)" }}>
                  {errorMsg}
                </p>
              )}

              <button
                type="submit"
                disabled={formState === "submitting" || !name.trim() || !email.trim()}
                className="w-full flex items-center justify-center gap-2 rounded-lg px-6 py-3 text-sm font-bold transition-all"
                style={{
                  background: formState === "submitting" ? "rgba(255,140,0,0.5)" : "#FF8C00",
                  color: "#0F1419",
                  cursor: formState === "submitting" ? "not-allowed" : "pointer",
                  transform: "scale(1)",
                }}
                onMouseDown={(e) => { e.currentTarget.style.transform = "scale(0.97)"; }}
                onMouseUp={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
              >
                {formState === "submitting" ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sending your guide…
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    Get the Free PDF Guide
                  </>
                )}
              </button>

              <div className="flex items-center justify-center gap-1.5 mt-2">
                <Lock className="w-3 h-3" style={{ color: "#556677" }} />
                <p className="text-xs text-center" style={{ color: "#556677" }}>
                  No spam. We'll email you the PDF link - nothing else.
                </p>
              </div>
            </form>
          </>
        ) : (
          /* Success state with social sharing */
          <SuccessState pdfUrl={pdfUrl} sourceSlug={sourceSlug} />
        )}
      </div>
    </section>
  );
}
